import { ProductSlugs } from '@deque/billing-utils';
import {
  Loader,
  Modal,
  ModalContent,
  SearchField
} from '@deque/cauldron-react';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AccessTypes } from '~/common/utils/billing-client/client-v2';
import { PRODUCT_ENTITLEMENTS } from '~/common/utils/entitlements';
import { getAccessString } from '~/modules/app/utils/access-utils';
import ContentToast from '~/modules/common/components/ContentToast';
import {
  getEnterpriseProductUsers,
  type SubscriptionMember
} from '~/modules/common/utils/admin-client/get-enterprise-product-users';
import styles from './EnterpriseExplorer.css';

interface SubscriptionMembersModalProps {
  token: string;
  enterpriseId: string;
  subscriptionId: string;
  productName: string;
  productSlug: string;
  onClose: () => void;
  adminsOnly?: boolean;
}

const SubscriptionMembersModal: React.FC<SubscriptionMembersModalProps> = ({
  token,
  enterpriseId,
  subscriptionId,
  productName,
  productSlug,
  onClose,
  adminsOnly
}) => {
  const { t } = useTranslation();
  const [productUsers, setProductUsers] = useState<SubscriptionMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const fetchIdRef = React.useRef(0);

  React.useEffect(() => {
    const fetchId = ++fetchIdRef.current;

    const fetchUsers = async () => {
      setLoading(true);
      setError(null);
      setProductUsers([]);

      try {
        const result = await getEnterpriseProductUsers({
          token,
          enterpriseId,
          subscriptionId
        });
        if (fetchId !== fetchIdRef.current) {
          return;
        }
        setProductUsers(result.users);
      } catch (err) {
        if (fetchId !== fetchIdRef.current) {
          return;
        }
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (fetchId === fetchIdRef.current) {
          setLoading(false);
        }
      }
    };

    fetchUsers();

    return () => {
      fetchIdRef.current++;
    };
  }, [token, enterpriseId, subscriptionId]);

  const baseUsers = useMemo(() => {
    return adminsOnly
      ? productUsers.filter(member => member.access_type === AccessTypes.ADMIN)
      : productUsers;
  }, [productUsers, adminsOnly]);

  const filteredProductUsers = useMemo(() => {
    if (!searchQuery) {
      return baseUsers;
    }
    const query = searchQuery.toLowerCase();
    return baseUsers.filter(member => {
      const firstName = (member.first_name ?? '').toLowerCase();
      const lastName = (member.last_name ?? '').toLowerCase();
      const firstLast = [firstName, lastName].filter(Boolean).join(' ');
      const lastFirst = [lastName, firstName].filter(Boolean).join(', ');
      const email = (member.email ?? '').toLowerCase();
      return (
        firstLast.includes(query) ||
        lastFirst.includes(query) ||
        email.includes(query) ||
        member.keycloak_id.toLowerCase().includes(query)
      );
    });
  }, [baseUsers, searchQuery]);

  const heading = adminsOnly
    ? t('Application Admins for {{product}}', { product: productName })
    : t('Users for {{product}}', { product: productName });

  const totalLabel = adminsOnly
    ? t('Total admins: {{total}}', { total: baseUsers.length })
    : t('Total members: {{total}}', { total: baseUsers.length });

  return (
    <Modal heading={heading} onClose={onClose} show>
      <ModalContent>
        {loading ? (
          <Loader label={t('Loading...')} tabIndex={-1} />
        ) : error ? (
          <ContentToast type="caution" show>
            <strong>
              {adminsOnly
                ? t('Error loading application admins -')
                : t('Error loading product users -')}
            </strong>
            <code>{error}</code>
          </ContentToast>
        ) : (
          <>
            <p>{totalLabel}</p>
            {baseUsers.length > 0 && (
              <SearchField
                label={adminsOnly ? t('Search admins') : t('Search users')}
                value={searchQuery}
                onChange={setSearchQuery}
              />
            )}
            <ul className={styles.userList}>
              {filteredProductUsers.length === 0 ? (
                <li className={styles.userListItem}>
                  <span>
                    {adminsOnly ? t('No admins found') : t('No users found')}
                  </span>
                </li>
              ) : (
                filteredProductUsers.map(member => {
                  const displayName = [member.last_name, member.first_name]
                    .filter(Boolean)
                    .join(', ');
                  const showMcpWrite =
                    productSlug === ProductSlugs.axeMonitor &&
                    member.entitlements !== undefined;
                  return (
                    <li
                      key={member.keycloak_id}
                      className={styles.userListItem}
                    >
                      <span>
                        {displayName || member.email || member.keycloak_id}
                      </span>
                      {!adminsOnly && (
                        <span className={styles.accessType}>
                          {member.access_type === AccessTypes.ADMIN ||
                          member.access_type === AccessTypes.GENERAL
                            ? getAccessString(t, member.access_type)
                            : member.access_type}
                        </span>
                      )}
                      {showMcpWrite && (
                        <span className={styles.accessType}>
                          {t('MCP write')}:{' '}
                          {member.entitlements?.[
                            PRODUCT_ENTITLEMENTS[ProductSlugs.axeMonitor]
                              .mcpWrite
                          ]
                            ? t('Yes')
                            : t('No')}
                        </span>
                      )}
                    </li>
                  );
                })
              )}
            </ul>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

export default SubscriptionMembersModal;
